import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, Download } from "lucide-react";

const STATUS_BADGE = {
  baru: "bg-emerald-50 text-emerald-700 border-emerald-200",
  duplikat: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_LABEL = { baru: "Baru", duplikat: "Duplikat", error: "Error" };

const PreviewTable = ({ rows, type, testid }) => (
  <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-[#267B84] text-white">
        <tr>
          <th className="text-left px-3 py-2 text-xs font-semibold w-10">No</th>
          {type === "dpjp" && <th className="text-left px-3 py-2 text-xs font-semibold">Departemen</th>}
          <th className="text-left px-3 py-2 text-xs font-semibold">{type === "ksm" ? "Nama Departemen" : "Nama DPJP"}</th>
          <th className="text-left px-3 py-2 text-xs font-semibold">Sub-spesialisasi</th>
          <th className="text-left px-3 py-2 text-xs font-semibold">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {rows.length === 0 ? (
          <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 text-sm">Tidak ada baris data.</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i} className={r.status === "baru" ? "" : "opacity-70"}>
            <td className="px-3 py-2 text-xs text-slate-400">{i + 1}</td>
            {type === "dpjp" && <td className="px-3 py-2 text-slate-600">{r.ksm}</td>}
            <td className="px-3 py-2 font-medium text-slate-800">{r.nama}</td>
            <td className="px-3 py-2 text-slate-500">{r.sub_spesialisasi || "—"}</td>
            <td className="px-3 py-2">
              <span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-semibold ${STATUS_BADGE[r.status]}`}>
                {STATUS_LABEL[r.status]}
              </span>
              <span className="block text-[11px] text-slate-400 mt-0.5">{r.catatan}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function ImportMasterDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setPreview(null);
    }
  }, [open]);

  const handlePreview = async () => {
    if (!file) {
      toast.error("Pilih file Excel terlebih dahulu");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/master/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleTemplate = async () => {
    try {
      const res = await api.get("/master/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_import_master_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template berhasil diunduh. Hapus baris contoh, isi data, lalu unggah kembali.");
    } catch (e) {
      toast.error("Gagal mengunduh template");
    }
  };

  const totalBaru = preview ? preview.ringkasan.ksm_baru + preview.ringkasan.dpjp_baru : 0;

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const payload = {
        ksm: preview.ksm.filter((r) => r.status === "baru"),
        dpjp: preview.dpjp.filter((r) => r.status === "baru"),
      };
      const { data } = await api.post("/master/import/commit", payload);
      toast.success(`Import selesai: ${data.inserted_ksm} Departemen & ${data.inserted_dpjp} DPJP ditambahkan (${data.skipped} dilewati)`);
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-white" data-testid="import-master-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Import Master Data dari Excel</DialogTitle>
          <DialogDescription className="text-xs">
            File .xlsx dengan 2 sheet: <strong>"Master Departemen"</strong> (kolom: No, Nama Departemen, Sub-spesialisasi) dan{" "}
            <strong>"Master DPJP"</strong> (kolom: No, Departemen, Sub-spesialisasi, Nama DPJP). Baris duplikat otomatis dilewati. Nama sheet/kolom lama ("Master KSM", "Nama KSM", "KSM") tetap diterima. Belum punya filenya? Klik <strong>"Download Template"</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleTemplate}
              data-testid="import-template-button"
            >
              <Download size={14} /> Download Template
            </Button>
            <label
              htmlFor="import-file-input"
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-600 transition-colors"
            >
              <FileSpreadsheet size={16} className="text-[#00B9AD]" />
              {file ? file.name : "Pilih file Excel (.xlsx)..."}
            </label>
            <input
              id="import-file-input"
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            />
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handlePreview}
              disabled={!file || loading}
              data-testid="import-preview-button"
            >
              <Upload size={14} /> {loading ? "Membaca..." : "Preview"}
            </Button>
          </div>

          {preview && (
            <>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="import-summary">
                <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                  {preview.ringkasan.ksm_baru} Departemen baru
                </span>
                <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                  {preview.ringkasan.dpjp_baru} DPJP baru
                </span>
                {(preview.ringkasan.ksm_duplikat + preview.ringkasan.dpjp_duplikat) > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                    {preview.ringkasan.ksm_duplikat + preview.ringkasan.dpjp_duplikat} duplikat dilewati
                  </span>
                )}
                {preview.ringkasan.dpjp_error > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold">
                    {preview.ringkasan.dpjp_error} baris error
                  </span>
                )}
              </div>

              <Tabs defaultValue="ksm">
                <TabsList className="bg-white border border-slate-200">
                  <TabsTrigger value="ksm" data-testid="import-tab-ksm"
                    className="data-[state=active]:bg-[#00B9AD] data-[state=active]:text-white">
                    Master Departemen ({preview.ksm.length})
                  </TabsTrigger>
                  <TabsTrigger value="dpjp" data-testid="import-tab-dpjp"
                    className="data-[state=active]:bg-[#00B9AD] data-[state=active]:text-white">
                    Master DPJP ({preview.dpjp.length})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="ksm" className="mt-3">
                  <PreviewTable rows={preview.ksm} type="ksm" testid="import-preview-table-ksm" />
                </TabsContent>
                <TabsContent value="dpjp" className="mt-3">
                  <PreviewTable rows={preview.dpjp} type="dpjp" testid="import-preview-table-dpjp" />
                </TabsContent>
              </Tabs>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="import-cancel-button">
              Tutup
            </Button>
            <Button
              onClick={handleCommit}
              disabled={!preview || totalBaru === 0 || committing}
              className="gap-1.5 bg-[#00B9AD] hover:bg-[#009E94] text-white"
              data-testid="import-commit-button"
            >
              <CheckCircle2 size={14} />
              {committing ? "Mengimport..." : `Import ${totalBaru} Data Baru`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
